import {
  ActionBar,
  ChakraBox,
  FAIconName,
  FormLabel,
  MultiSelectOption,
  UniversalSelect,
} from '@equip.health/ui';
import { useMemo, type Dispatch, type SetStateAction } from 'react';

import {
  CANCEL_APPOINTMENT_API_ERROR_TEXT,
  CANCELLATION_APPOINTMENT_REASON,
  CANCELLATION_APPOINTMENT_SUB_REASON,
} from '~/lib/constants/calendar.constants';
import { useGetCancellationReasonsQuery } from '~/store/services/appointment';

export type CancellationReasonOption = MultiSelectOption & {
  label: string;
  value: string;
  subReasons?: MultiSelectOption[];
};

interface AppointmentCancelReasonProps {
  id: string;
  selectedReason: CancellationReasonOption;
  setSelectedReason: Dispatch<
    SetStateAction<CancellationReasonOption | undefined>
  >;
  selectedSubReason: MultiSelectOption | undefined;
  setSelectedSubReason: Dispatch<SetStateAction<MultiSelectOption | undefined>>;
  isCancelError: boolean;
}

const AppointmentCancelReason = ({
  id,
  selectedReason,
  setSelectedReason,
  selectedSubReason,
  setSelectedSubReason,
  isCancelError,
}: AppointmentCancelReasonProps) => {
  const { data: cancellationReasons } = useGetCancellationReasonsQuery();

  const formattedCancellationReasons = useMemo(() => {
    return (cancellationReasons?.data ?? []).map((reason) => ({
      label: reason.reason,
      subReasons: (reason.subReasons ?? []).map((subReason) => ({
        label: subReason.subReason,
        value: subReason.code,
      })),
      value: reason.code,
    }));
  }, [cancellationReasons]);

  return (
    <>
      <ChakraBox width="100%">
        <FormLabel
          id={`${id}__cancellation-reason-label`}
          label={CANCELLATION_APPOINTMENT_REASON}
          shouldShowAsteriskWhenRequired
        />
        <ChakraBox marginTop="6px">
          <UniversalSelect
            id={`${id}__cancel-reasons`}
            isSingleSelect
            modalId={id}
            onChange={(val) => {
              setSelectedReason(val);
              setSelectedSubReason(undefined);
            }}
            options={formattedCancellationReasons}
            placeholder="Please choose"
          />
        </ChakraBox>
      </ChakraBox>
      {selectedReason?.subReasons?.length > 0 && (
        <ChakraBox marginTop="12px" width="100%">
          <FormLabel
            id={`${id}__cancellation-sub-reason-label`}
            label={CANCELLATION_APPOINTMENT_SUB_REASON}
            shouldShowAsteriskWhenRequired
          />
          <ChakraBox marginTop="6px">
            <UniversalSelect
              id={`${id}__cancel-subreasons`}
              isSingleSelect
              modalId={id}
              value={selectedSubReason}
              onChange={(val) => setSelectedSubReason(val)}
              options={selectedReason?.subReasons}
              placeholder="Please choose"
            />
          </ChakraBox>
        </ChakraBox>
      )}
      {isCancelError && (
        <ChakraBox marginTop="16px">
          <ActionBar
            barIcon={FAIconName.exclamationTriangleRegular}
            id={`${id}__error-message`}
            text={CANCEL_APPOINTMENT_API_ERROR_TEXT}
            variant="critical"
          />
        </ChakraBox>
      )}
    </>
  );
};

export default AppointmentCancelReason;
